import React from 'react';
import { MapPin, Calendar, Users, AlertTriangle } from 'lucide-react';
import { Chantier } from '../utils/types';
import { Card } from './ui/Card';
import { StatusBadge } from './ui/StatusBadge';
import { useNavigate } from 'react-router-dom';
import { getClientById, getChefEquipe } from '../utils/mockData';
import { motion } from 'framer-motion';
interface ChantierCardProps {
  chantier: Chantier;
}
const workTypeLabels: Record<string, string> = {
  signaletique_interieure: 'Signalétique intérieure',
  signaletique_exterieure: 'Signalétique extérieure',
  marquage_sol: 'Marquage au sol',
  revetement: 'Revêtement / Résine',
  peinture_decorative: 'Peinture décorative',
  mobilier: 'Installation mobilier',
  amenagement_commercial: 'Aménagement commercial',
  maintenance_preventive: 'Maintenance préventive',
  intervention_corrective: 'Intervention corrective',
  renovation_partielle: 'Rénovation partielle'
};
export function ChantierCard({
  chantier
}: ChantierCardProps) {
  const navigate = useNavigate();
  const client = getClientById(chantier.clientId);
  const chefEquipe = getChefEquipe(chantier.chefEquipeId);
  return <Card className="overflow-hidden group" onClick={() => navigate(`/chantiers/${chantier.id}`)} data-id="element-71">
      {/* Image */}
      <div className="h-44 sm:h-48 w-full bg-gray-200 relative overflow-hidden" data-id="element-72">
        {chantier.tasks.flatMap(t => t.images).length > 0 ? <img src={chantier.tasks.flatMap(t => t.images)[0].url} alt={chantier.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-73" /> : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200" data-id="element-74">
            <MapPin size={48} className="text-gray-300" data-id="element-75" />
          </div>}
        <div className="absolute top-3 right-3 flex gap-2" data-id="element-76">
          <StatusBadge status={chantier.status} data-id="element-77" />
          {chantier.priority === 'urgent' && <span className="px-2.5 py-1 bg-amber-500 text-white text-xs font-medium rounded-full flex items-center gap-1" data-id="element-78">
              <AlertTriangle size={12} data-id="element-79" />
              Urgent
            </span>}
          {chantier.priority === 'critique' && <span className="px-2.5 py-1 bg-red-500 text-white text-xs font-medium rounded-full flex items-center gap-1" data-id="element-80">
              <AlertTriangle size={12} data-id="element-81" />
              Critique
            </span>}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-5 space-y-4" data-id="element-82">
        {/* Title & Location */}
        <div data-id="element-83">
          <h3 className="heading-sm mb-1 truncate group-hover:text-[#1DBAB4] transition-colors" data-id="element-84">
            {chantier.name}
          </h3>
          <div className="flex items-center text-caption gap-1 mb-2" data-id="element-85">
            <MapPin size={14} className="flex-shrink-0" data-id="element-86" />
            <span className="truncate" data-id="element-87">
              {chantier.city}, {chantier.country}
            </span>
          </div>
          <span className="inline-block px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded" data-id="element-88">
            {workTypeLabels[chantier.workType]}
          </span>
        </div>

        {/* Client */}
        {client && <div className="flex items-center gap-2 p-2 bg-teal-50 rounded-lg border border-teal-100" data-id="element-89">
            <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-90">
              <span className="text-xs font-bold text-teal-700" data-id="element-91">
                {client.raisonSociale.charAt(0)}
              </span>
            </div>
            <div className="flex-1 min-w-0" data-id="element-92">
              <p className="text-xs font-medium text-teal-900 truncate" data-id="element-93">
                {client.raisonSociale}
              </p>
            </div>
          </div>}

        {/* Chef d'équipe */}
        {chefEquipe && <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100" data-id="element-94">
            <img src={chefEquipe.avatar} alt={chefEquipe.name} className="w-6 h-6 rounded-full ring-2 ring-white flex-shrink-0" data-id="element-95" />
            <div className="flex-1 min-w-0" data-id="element-96">
              <p className="text-xs font-medium text-amber-900 truncate" data-id="element-97">
                {chefEquipe.name}
              </p>
              <p className="text-[10px] text-amber-600" data-id="element-98">Chef d'équipe</p>
            </div>
          </div>}

        {/* Meta Info */}
        <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600" data-id="element-99">
          <div className="flex items-center gap-1.5" data-id="element-100">
            <Calendar size={14} className="flex-shrink-0 text-gray-400" data-id="element-101" />
            <span className="truncate" data-id="element-102">
              {new Date(chantier.startDate).toLocaleDateString('fr-FR')}
            </span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-103">
            <Users size={14} className="flex-shrink-0 text-gray-400" data-id="element-104" />
            <span data-id="element-105">{chantier.team.length} techniciens</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2" data-id="element-106">
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden" data-id="element-107">
            <motion.div className="bg-[#1DBAB4] h-2 rounded-full" initial={{
            width: 0
          }} animate={{
            width: `${chantier.progress}%`
          }} transition={{
            duration: 1,
            ease: 'easeOut'
          }} data-id="element-108" />
          </div>
          <div className="flex justify-between text-xs" data-id="element-109">
            <span className="text-gray-500" data-id="element-110">Progression</span>
            <span className="font-semibold text-gray-900" data-id="element-111">
              {chantier.progress}%
            </span>
          </div>
        </div>
      </div>
    </Card>;
}